import { CalculatorSection } from "@/components/public/CalculatorSection"
import { CtaSection } from "@/components/public/CtaSection"
import { LandingFooter } from "@/components/public/LandingFooter"
import { PublicHeader } from "@/components/public/PublicHeader"
import { StepsSection } from "@/components/public/StepsSection"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { BreathingGradient } from "@/components/ui/living-brand"
import { SpotlightCard } from "@/components/ui/SpotlightCard"
import { Heading, Text } from "@/components/ui/typography"
import { useReducedMotion } from "@/hooks/useReducedMotion"
import { sfIcons } from '@/lib/sf-icons'
import { cn } from "@/lib/utils"
import { semanticTokens } from "@packages/config/theme"
import { Page } from "@supabank/ui"
import { motion } from "framer-motion"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

// Canonical icon mapping using sfIcons
const iconMap = {
  Users: sfIcons.users,
  BarChart3: sfIcons.barChart3,
  FileText: sfIcons.fileText,
  Shield: sfIcons.security,
  Zap: sfIcons.zap,
  Globe: sfIcons.global2,
  TrendingUp: sfIcons.trendingUp,
  Wallet: sfIcons.wallet,
  Network: sfIcons.network,
  Database: sfIcons.database,
  Webhook: sfIcons.webhook
} as const

export default function ForFinanceTeamsPage() {
  const prefersReducedMotion = useReducedMotion()
  const { t } = useTranslation("marketing")

  // Get translated features from i18n
  const features = (t("forFinanceTeamsPage.features", { returnObjects: true }) as Array<{
    title: string
    description: string
    icon: keyof typeof iconMap
  }>)

  // Get translated use cases from i18n
  const useCases = (t("forFinanceTeamsPage.useCases", { returnObjects: true }) as Array<{
    title: string
    description: string
    icon: keyof typeof iconMap
  }>)

  // Get translated capabilities from i18n
  const capabilities = (t("forFinanceTeamsPage.capabilities", { returnObjects: true }) as Array<{
    title: string
    description: string
    icon: keyof typeof iconMap
  }>)

  return (
    <Page data-ux-contract="canon" role="main" className="bg-background text-foreground">
      <PublicHeader />

      <main className="space-y-0" id="main" role="main">
        {/* Hero Section */}
        <section className={cn(
          "relative overflow-hidden",
          "py-16 sm:py-24 xl:py-32",
          // CANON: Hero gradient background (lavenderGlow for fintech feel)
          semanticTokens.heroGradient.lavenderGlow.tailwind,
          // Dark mode variant
          "dark:bg-black dark:bg-[radial-gradient(ellipse_at_20%_50%,hsla(270,95%,75%,0.08)_0%,transparent_50%),radial-gradient(ellipse_at_80%_80%,hsla(217,91%,60%,0.06)_0%,transparent_50%),hsl(var(--background))]"
        )}>
          {/* Living Brand: Breathing Gradient (syncs with LED heartbeat) - DISABLED on dark theme */}
          <BreathingGradient
            variant="lavender"
            syncWithLed
            className="absolute inset-0 z-[1] pointer-events-none opacity-30 dark:hidden"
          />
          <div className="relative z-10 container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <Badge variant="outline" className="mb-4">
                {t("forFinanceTeamsPage.badge")}
              </Badge>

              <Heading level={1} className="mb-4">
                {t("forFinanceTeamsPage.headline")}
              </Heading>

              <Text variant="lg" color="muted" className="max-w-2xl mx-auto">
                {t("forFinanceTeamsPage.subheadline")}
              </Text>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                <Button asChild size="lg" className="gap-2 min-h-[44px] touch-manipulation">
                  <Link to="/auth">
                    {t("forFinanceTeamsPage.cta.scheduleDemo")}
                    <sfIcons.next className="w-4 h-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="min-h-[44px] touch-manipulation">
                  <a
                    href="#calculator"
                    onClick={(e) => {
                      e.preventDefault();
                      const element = document.getElementById('calculator');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                  >
                    {t("forFinanceTeamsPage.cta.seePricing")}
                  </a>
                </Button>
              </div>

              {/* Trust signals */}
              <div className="flex flex-wrap justify-center gap-6 mt-12 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <sfIcons.success className="w-4 h-4 text-emerald-500" />
                  <span>{t("forFinanceTeamsPage.trustSignals.amlKyc")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <sfIcons.success className="w-4 h-4 text-emerald-500" />
                  <span>{t("forFinanceTeamsPage.trustSignals.auditTrails")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <sfIcons.success className="w-4 h-4 text-emerald-500" />
                  <span>{t("forFinanceTeamsPage.trustSignals.multiCurrency")}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Key Features Section */}
        <section className={cn(
          "py-16 sm:py-24 relative overflow-hidden",
          semanticTokens.bg.default
        )}>
          {/* Subtle background gradient */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                radial-gradient(ellipse at 20% 50%, hsla(217, 91%, 60%, 0.05) 0%, transparent 50%),
                radial-gradient(ellipse at 80% 50%, hsla(270, 95%, 75%, 0.05) 0%, transparent 50%)
              `
            }}
          />
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-6xl mx-auto space-y-12">
              <motion.div
                className="text-center space-y-4"
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.24 }}
              >
                <Heading level={2}>{t("forFinanceTeamsPage.featuresSection.headline")}</Heading>
                <Text variant="lg" color="muted">
                  {t("forFinanceTeamsPage.featuresSection.subheadline")}
                </Text>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {features.map((feature, index) => {
                  const Icon = iconMap[feature.icon] || sfIcons.security
                  return (
                    <motion.div
                      key={feature.title}
                      initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                      whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.24, delay: index * 0.1 }}
                    >
                      <SpotlightCard
                        className={cn(
                          "h-full p-6 sm:p-8",
                          semanticTokens.motion.standard,
                          "hover:shadow-xl hover:-translate-y-1"
                        )}
                      >
                        <div className="space-y-4">
                          <div className={cn(
                            "inline-flex h-14 w-14 items-center justify-center rounded-xl transition-all duration-240",
                            "bg-gradient-to-br from-primary/10 to-primary/5",
                            "border-2 border-primary/20 text-primary",
                            "group-hover:bg-gradient-to-br group-hover:from-primary/20 group-hover:to-primary/10",
                            "group-hover:border-primary/40 group-hover:scale-105",
                            "shadow-sm group-hover:shadow-md"
                          )}>
                            <Icon className="h-7 w-7" aria-hidden="true" />
                          </div>
                          <div className="space-y-2">
                            <Heading level={3} className="text-lg font-display">
                              {feature.title}
                            </Heading>
                            <Text variant="sm" color="muted" className="leading-relaxed">
                              {feature.description}
                            </Text>
                          </div>
                        </div>
                      </SpotlightCard>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className={cn(
          "py-16 sm:py-24 relative overflow-hidden",
          semanticTokens.bg.surface
        )}>
          {/* Subtle background gradient */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                radial-gradient(ellipse at 10% 90%, hsla(217, 91%, 60%, 0.05) 0%, transparent 45%),
                radial-gradient(ellipse at 90% 10%, hsla(270, 95%, 75%, 0.05) 0%, transparent 45%)
              `
            }}
          />
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-6xl mx-auto space-y-12">
              <motion.div
                className="text-center space-y-4"
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.24 }}
              >
                <Heading level={2}>{t("forFinanceTeamsPage.useCasesSection.headline")}</Heading>
                <Text variant="lg" color="muted">
                  {t("forFinanceTeamsPage.useCasesSection.subheadline")}
                </Text>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {useCases.map((useCase, index) => {
                  const Icon = iconMap[useCase.icon] || sfIcons.users
                  return (
                    <motion.div
                      key={useCase.title}
                      initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                      whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.24, delay: index * 0.1 }}
                    >
                      <SpotlightCard
                        className={cn(
                          "h-full p-6 sm:p-8",
                          semanticTokens.motion.standard,
                          "hover:shadow-xl hover:-translate-y-1"
                        )}
                      >
                        <div className="space-y-4">
                          <div className={cn(
                            "inline-flex h-14 w-14 items-center justify-center rounded-xl transition-all duration-240",
                            "bg-gradient-to-br from-primary/10 to-primary/5",
                            "border-2 border-primary/20 text-primary",
                            "group-hover:bg-gradient-to-br group-hover:from-primary/20 group-hover:to-primary/10",
                            "group-hover:border-primary/40 group-hover:scale-105",
                            "shadow-sm group-hover:shadow-md"
                          )}>
                            <Icon className="h-7 w-7" aria-hidden="true" />
                          </div>
                          <div className="space-y-2">
                            <Heading level={3} className="text-lg font-display">
                              {useCase.title}
                            </Heading>
                            <Text variant="sm" color="muted" className="leading-relaxed">
                              {useCase.description}
                            </Text>
                          </div>
                        </div>
                      </SpotlightCard>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Capabilities Section */}
        <section className={cn(
          "py-16 sm:py-24 relative overflow-hidden",
          semanticTokens.bg.default
        )}>
          {/* Subtle background gradient */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                radial-gradient(ellipse at 50% 20%, hsla(217, 91%, 60%, 0.05) 0%, transparent 50%),
                radial-gradient(ellipse at 50% 80%, hsla(270, 95%, 75%, 0.05) 0%, transparent 50%)
              `
            }}
          />
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-6xl mx-auto space-y-12">
              <motion.div
                className="text-center space-y-4"
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.24 }}
              >
                <Heading level={2}>{t("forFinanceTeamsPage.capabilitiesSection.headline")}</Heading>
                <Text variant="lg" color="muted">
                  {t("forFinanceTeamsPage.capabilitiesSection.subheadline")}
                </Text>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {capabilities.map((capability, index) => {
                  const Icon = iconMap[capability.icon] || sfIcons.fileText
                  return (
                    <motion.div
                      key={capability.title}
                      initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                      whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.24, delay: index * 0.1 }}
                    >
                      <SpotlightCard
                        className={cn(
                          "h-full p-6 sm:p-8",
                          semanticTokens.motion.standard,
                          "hover:shadow-xl hover:-translate-y-1"
                        )}
                      >
                        <div className="space-y-4">
                          <div className={cn(
                            "inline-flex h-14 w-14 items-center justify-center rounded-xl transition-all duration-240",
                            "bg-gradient-to-br from-primary/10 to-primary/5",
                            "border-2 border-primary/20 text-primary",
                            "group-hover:bg-gradient-to-br group-hover:from-primary/20 group-hover:to-primary/10",
                            "group-hover:border-primary/40 group-hover:scale-105",
                            "shadow-sm group-hover:shadow-md"
                          )}>
                            <Icon className="h-7 w-7" aria-hidden="true" />
                          </div>
                          <div className="space-y-2">
                            <Heading level={3} className="text-lg font-display">
                              {capability.title}
                            </Heading>
                            <Text variant="sm" color="muted" className="leading-relaxed">
                              {capability.description}
                            </Text>
                          </div>
                        </div>
                      </SpotlightCard>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        {/* How It Works - Simplified */}
        <StepsSection id="how-it-works" />

        {/* Calculator Section */}
        <CalculatorSection id="calculator" />

        {/* Enterprise CTA */}
        <section className={cn("py-16 sm:py-24", semanticTokens.bg.default)}>
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <motion.div
                initial={prefersReducedMotion ? {} : { opacity: 0 }}
                whileInView={prefersReducedMotion ? {} : { opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.24 }}
                className="space-y-4"
              >
                <div className={cn(
                  "inline-flex items-center justify-center w-16 h-16 rounded-full mb-4",
                  semanticTokens.accent.primary
                )}>
                  <sfIcons.building2 className={cn("w-8 h-8", semanticTokens.fg.inverted)} />
                </div>
                <Heading level={2}>{t("forFinanceTeamsPage.enterpriseCta.headline")}</Heading>
                <Text variant="lg" color="muted">
                  {t("forFinanceTeamsPage.enterpriseCta.subheadline")}
                </Text>
                <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                  <Button asChild size="lg" className="gap-2">
                    <Link to="/auth">
                      {t("forFinanceTeamsPage.enterpriseCta.contactSales")}
                      <sfIcons.next className="w-4 h-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="lg">
                    <a href={`mailto:${t("forFinanceTeamsPage.enterpriseCta.email")}`}>
                      {t("forFinanceTeamsPage.enterpriseCta.email")}
                    </a>
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <CtaSection id="cta" />
      </main>

      <LandingFooter />
    </Page>
  )
}
